import React, { useState, useEffect } from "react";
import DeviceList from "./DeviceList";
import DeviceDetail from "./DeviceDetail";
import ThemeToggle from "./ThemeToggle";
import { createClient, getItems, getLoginInfo } from "../api";

export default function Dashboard({ token, setToken }) {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [streamModes, setStreamModes] = useState({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [editTarget, setEditTarget] = useState(undefined);
  const [form, setForm] = useState({ deviceName: "", ipAddress: "", rtmPpushLink: "" });
  const [theme, setTheme] = useState("light");
  const apiClient = createClient(token);

  useEffect(() => {
    getLoginInfo(token)
      .then((info) => setUser(info.user || {}))
      .catch((err) => console.error("Failed to load profile", err));
  }, [token]);

  // Load theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const items = await getItems(apiClient, "/DeviceSensor/GetAll");
      setDevices(items);
      setStreamModes((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          if (!next[item.id]) next[item.id] = "webrtc";
        });
        return next;
      });
    } catch (err) {
      console.error("Failed to fetch devices", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    setToken(null);
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const openCreateModal = () => {
    setEditTarget(null);
    setForm({ deviceName: "", ipAddress: "", rtmPpushLink: ""});
  };

  const openEditModal = (device) => {
    setEditTarget(device);
    setForm({
      deviceName: device.deviceName || "",
      ipAddress: device.ipAddress || "",
      rtmPpushLink: device.rtmPpushLink || "",
    });
  };

  const closeModal = () => {
    setEditTarget(undefined);
  };

  // const saveDevice = async () => {
  //   if (!apiClient) return;
  //   const payload = {
  //     ...(editTarget || {}),
  //     deviceName: form.deviceName,
  //     rtmPpushLink: form.rtmPpushLink,
  //     ipAddress: form.ipAddress,
  //     // routeId: Number(form.routeId || 0),
  //   };
  //   try {
  //     if (editTarget) {
  //       await apiClient.put("/DeviceSensor/Update", payload);
  //     } else {
  //       await apiClient.post("/DeviceSensor/Create", payload);
  //     }
  //     closeModal();
  //     await fetchDevices();
  //   } catch (err) {
  //     console.error("Save device failed", err);
  //     alert("Device save failed. Please check required API fields.");
  //   }
  // };
const saveDevice = async () => {
  if (!apiClient) return;

  const payload = {
    ...(editTarget || {}),
    deviceName: form.deviceName,
    ipAddress: form.ipAddress,
    rtmPpushLink: form.rtmPpushLink,
  };

  try {
    let deviceId;

    if (editTarget) {
      await apiClient.put("/DeviceSensor/Update", payload);
      deviceId = editTarget.id;
    } else {
      const res = await apiClient.post("/DeviceSensor/Create", payload);
      deviceId = res?.data?.result?.id;
    }

    // ✅ Create DeviceScene
    const sceneRes = await apiClient.post("/DeviceScene/Create", {
      deviceSensorId: deviceId,
      sceneConfigHeight: 1080,
      sceneConfigWidth: 1920,
      isRoifilteringstream: true,
      isLineCrossingStream: true,
    });

    const deviceSceneId = sceneRes?.data?.result?.id;

    // ✅ Create ROI Filtering
    await apiClient.post("/DeviceRoiFilteringStreamGate/Create", {
      deviceSceneId,
      isEnable: "1",
      isInverseRoi: "0",
      classId: "-1",
      roiGatePointxywh: "0;0;100;100",
    });

    // ✅ Create Line Crossing
    await apiClient.post("/DeviceLineCrossingStream/Create", {
      deviceSceneId,
      isEnable: true,
      direction: "both",
      classID: "-1",
      triggerInterval: "1000",
      lineCrossingSP: "0,0",
      lineCrossingEP: "100,100",
    });

    closeModal();
    await fetchDevices();
  } catch (err) {
    console.error("Save device failed", err);
    alert("Device save failed.");
  }
};
  // const deleteDevice = async (device) => {
  //   if (!apiClient) return;
  //   if (!window.confirm(`Delete device ${device.deviceName || device.id}?`)) return;
  //   try {
  //     await apiClient.delete("/DeviceSensor/Delete", {
  //       params: { id: device.id },
  //     });
  //     if (selectedDevice?.id === device.id) setSelectedDevice(null);
  //     await fetchDevices();
  //   } catch (err) {
  //     console.error("Delete failed", err);
  //     alert("Delete failed.");
  //   }
  // };
const deleteDevice = async (device) => {
  if (!apiClient) return;
  if (!window.confirm(`Delete device ${device.deviceName || device.id}?`)) return;

  try {
    // ✅ Get DeviceScene by deviceSensorId
    const scenes = await getItems(apiClient, `/DeviceScene/GetAll?deviceSensorId=${device.id}`);
    const scene = scenes?.[0];

    if (scene) {
      const sceneId = scene.id;

      // ✅ Delete ROI
      await apiClient.delete("/DeviceRoiFilteringStreamGate/Delete", {
        params: { deviceSceneId: sceneId },
      });

      // ✅ Delete Line Crossing
      await apiClient.delete("/DeviceLineCrossingStream/Delete", {
        params: { deviceSceneId: sceneId },
      });

      // ✅ Delete Scene
      await apiClient.delete("/DeviceScene/Delete", {
        params: { id: sceneId },
      });
    }

    // ✅ Delete DeviceSensor
    await apiClient.delete("/DeviceSensor/Delete", {
      params: { id: device.id },
    });

    if (selectedDevice?.id === device.id) setSelectedDevice(null);

    await fetchDevices();
  } catch (err) {
    console.error("Delete failed", err);
    alert("Delete failed.");
  }
};
  const setDeviceStreamMode = (deviceId, mode) => {
    setStreamModes((prev) => ({ ...prev, [deviceId]: mode }));
  };

  /** Keep list + selected row in sync after DeviceDetail saves overlay flags (and device row). */
  const handleDeviceSensorUpdated = (updated) => {
    if (!updated || updated.id == null) return;
    setDevices((prev) =>
      prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d))
    );
    setSelectedDevice((prev) =>
      prev && prev.id === updated.id ? { ...prev, ...updated } : prev
    );
  };

  return (
    <div className={`dashboard ${theme}`}>
      <header className="dashboard-header">
        <h2>AI Camera</h2>
        {user?.name && <h2>Welcome, {user.name}</h2>}
        <div className="header-actions">
          {/* <ThemeToggle theme={theme} setTheme={handleThemeChange} /> */}
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="main-content">
        <div className="left-panel">
          {!selectedDevice ? (
            <>
              {loading && <p>Loading devices...</p>}
              <DeviceList
                devices={devices}
                streamModes={streamModes}
                onChangeStreamMode={setDeviceStreamMode}
                onOpen={(device) =>
                  setSelectedDevice({
                    ...device,
                    streamMode: streamModes[device.id] || "webrtc",
                  })
                }
                onEdit={openEditModal}
                onDelete={deleteDevice}
                onAdd={openCreateModal}
              />
            </>
          ) : (
            <div className="device-detail-wrapper">
              <div className="title-row">
                <h3>Live Device View</h3>
                <button onClick={() => setSelectedDevice(null)}>Back</button>
              </div>
              <DeviceDetail
                device={selectedDevice}
                token={token}
                streamMode={selectedDevice.streamMode || "webrtc"}
                onDeviceSensorUpdated={handleDeviceSensorUpdated}
              />
            </div>
          )}
        </div>
      </div>

      {editTarget !== undefined && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>{editTarget ? "Edit Device" : "Add Device"}</h3>
            <label>
              DeviceSensor Name
              <input
                value={form.deviceName}
                onChange={(e) => setForm({ ...form, deviceName: e.target.value })}
              />
            </label>
            <label>
              Camera Source URL (RTSP)
              <input
                value={form.ipAddress}
                onChange={(e) => setForm({ ...form, ipAddress: e.target.value })}
                placeholder="rtsp://185.188.249.12:13101/h264"
              />
            </label>
            <label>
              RTMP Push Link (To MediaMTX)
              <input
                value={form.rtmPpushLink}
                onChange={(e) => setForm({ ...form, rtmPpushLink: e.target.value })}
                placeholder="rtmp://..."
              />
            </label>
            
            <div className="actions-row">
              <button className="primary-btn" onClick={saveDevice}>
                Save
              </button>
              <button onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}